import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart, Star, Minus, Plus, Truck, Tag, Sparkles, Play, ExternalLink, Heart } from 'lucide-react';
import { ProductGrid } from '../components/products/ProductGrid';
import { WishlistButton } from '../components/products/WishlistButton';
import { ReviewSection } from '../components/products/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [shippingSettings, setShippingSettings] = useState({ free_shipping_threshold: 50 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/settings`)
        ]);
        setProduct(productRes.data);
        setShippingSettings(settingsRes.data);
        
        // Fetch related products from same category
        if (productRes.data.category_id) {
          const relatedRes = await axios.get(`${API}/products?category_id=${productRes.data.category_id}&limit=4`);
          // Filter out current product and limit to 4
          const filtered = relatedRes.data.filter(p => p.id !== id).slice(0, 4);
          setRelatedProducts(filtered);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (product?.is_sold) {
      toast.error('This item has been sold');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    setAddingToCart(true);
    const success = await addToCart(product.id, quantity);
    setAddingToCart(false);
    
    if (success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-muted rounded-lg" />
          <div className="space-y-4">
            <div className="h-6 bg-muted rounded w-24" />
            <div className="h-10 bg-muted rounded w-3/4" />
            <div className="h-8 bg-muted rounded w-32" />
            <div className="h-24 bg-muted rounded" />
            <div className="h-12 bg-muted rounded w-48" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-testid="product-detail-page">
      {/* Back link */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 sm:mb-6 transition-colors text-sm sm:text-base"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-12">
        {/* Product Image */}
        <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
          <img
            src={product.image_url || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
            alt={product.name}
            className={`w-full h-full object-cover ${product.is_sold ? 'grayscale' : ''}`}
          />
          {/* SOLD Overlay */}
          {product.is_sold && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Badge className="bg-red-600 text-white text-lg sm:text-2xl px-4 sm:px-6 py-2 sm:py-3 font-bold shadow-xl transform -rotate-12" data-testid="sold-badge">
                SOLD
              </Badge>
            </div>
          )}
          <div className="absolute top-3 sm:top-4 left-3 sm:left-4 flex flex-col gap-2">
            {product.is_bestseller && !product.is_sold && (
              <Badge className="bg-accent text-accent-foreground flex items-center gap-1 text-xs sm:text-sm w-fit">
                <Star className="h-3 w-3 sm:h-4 sm:w-4 fill-current" />
                Bestseller
              </Badge>
            )}
            {product.is_new_arrival && !product.is_sold && (
              <Badge className="bg-emerald-600 text-white flex items-center gap-1 text-xs sm:text-sm w-fit" data-testid="new-arrival-badge">
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                New Arrival
              </Badge>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="animate-fade-in">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">{product.category_name}</p>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4" data-testid="product-name">
            {product.name}
          </h1>
          
          <p className="price-tag text-2xl sm:text-3xl font-bold text-primary mb-3 sm:mb-4" data-testid="product-price">
            ${product.price.toFixed(2)}
          </p>

          {/* Shipping info badge */}
          {product.price >= shippingSettings.free_shipping_threshold ? (
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Free Shipping
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <Tag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Add ${(shippingSettings.free_shipping_threshold - product.price).toFixed(2)} more for free shipping
            </div>
          )}

          <p className="text-sm sm:text-base text-foreground/80 mb-4 sm:mb-6 leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          {/* Quantity selector */}
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <span className="text-xs sm:text-sm font-medium">Quantity:</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 sm:h-9 sm:w-9"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                data-testid="decrease-qty"
              >
                <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <span className="w-10 sm:w-12 text-center font-medium text-sm sm:text-base" data-testid="quantity-display">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 sm:h-9 sm:w-9"
                onClick={() => setQuantity(quantity + 1)}
                data-testid="increase-qty"
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>

          {/* Add to cart / Affiliate button */}
          <div className="flex flex-col gap-2 sm:gap-3">
            {product.is_sold ? (
              <Button
                size="lg"
                className="w-full bg-gray-400 cursor-not-allowed"
                disabled
                data-testid="sold-btn"
              >
                <Tag className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Item Sold
              </Button>
            ) : product.affiliate_url ? (
              <a href={product.affiliate_url} target="_blank" rel="noopener noreferrer" className="w-full">
                <Button
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="affiliate-link-btn"
                >
                  <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Buy from Partner — ${product.price.toFixed(2)}
                </Button>
              </a>
            ) : (
              <Button
                size="lg"
                className="btn-accent w-full"
                onClick={handleAddToCart}
                disabled={addingToCart}
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner h-4 w-4" />
                    Adding...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                    Add to Cart
                  </span>
                )}
              </Button>
            )}
            
            {/* Wishlist Button - show even for sold items */}
            <WishlistButton 
              itemId={product.id} 
              itemType="product" 
              variant="outline"
              size="lg"
              showText
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Product Video */}
      {product.video_url && (
        <section className="mt-12" data-testid="product-video-section">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Play className="h-5 w-5 text-primary" />
            Product Video
          </h2>
          <div className="rounded-lg overflow-hidden bg-muted max-w-3xl">
            {product.video_url.includes('youtube.com') || product.video_url.includes('youtu.be') ? (
              <iframe
                src={product.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                title={`${product.name} video`}
                className="w-full aspect-video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : product.video_url.includes('vimeo.com') ? (
              <iframe
                src={product.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                title={`${product.name} video`}
                className="w-full aspect-video"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={product.video_url}
                controls
                className="w-full aspect-video"
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <ReviewSection itemId={id} itemType="product" />

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="mt-16 pt-8 border-t" data-testid="related-products">
          <h2 className="font-serif text-2xl font-bold text-foreground mb-6">
            You Might Also Like
          </h2>
          <ProductGrid products={relatedProducts} />
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
